/**
 * Публичная инструкция «Умный импорт» — addprovider.ru/docs.
 * Контент — в ./docs-content (правим текст там). Стиль/верстка — здесь
 * (тёмный фон ink-950, белый текст, красный accent, sticky TOC) — как на add-price.ru/docs.
 */
import { useEffect, useState } from 'react';
import { ArrowRight, Mail, Send } from 'lucide-react';
import {
  helpChangelog,
  helpFaq,
  helpSections,
  helpTableOfContents,
  supportEmail,
  supportTelegram,
} from './docs-content';

const INSTALL_URL = 'https://online.moysklad.ru/app/#apps?id=c5652470-2668-4096-ac28-cf73b6ef6592';

export const DocsPage = (): React.JSX.Element => {
  const [active, setActive] = useState<string>('overview');

  useEffect(() => {
    const handler = (): void => {
      const offsets = helpTableOfContents.map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return { id: s.id, top: Infinity };
        return { id: s.id, top: Math.abs(el.getBoundingClientRect().top - 100) };
      });
      const min = offsets.reduce((a, b) => (a.top < b.top ? a : b));
      setActive(min.id);
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <section className="container py-12 md:py-16">
      <div className="max-w-3xl mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-ink-700 bg-ink-900 px-3 py-1 text-xs text-ink-300 mb-5">
          <span className="dot" />
          инструкция
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
          Как пользоваться «Умным импортом»
        </h1>
        <p className="mt-4 text-ink-300 text-lg">
          Что приложение умеет, как настроить поставщика, откуда брать прайс, как считаются цены и
          остатки, предпросмотр и что делать, если что-то пошло не так.
        </p>
        <div className="mt-6 flex gap-3">
          <a
            href={INSTALL_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-accent hover:bg-accent-hover px-5 py-2.5 text-sm font-medium text-white transition-colors"
          >
            Установить из МойСклад
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block sticky top-20 self-start">
          <h2 className="text-xs uppercase tracking-wider text-ink-400 font-medium mb-3 px-2">
            Содержание
          </h2>
          <nav className="space-y-0.5">
            {helpTableOfContents.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={
                  active === s.id
                    ? 'block rounded-md px-2 py-1.5 text-sm font-medium bg-ink-800 text-white'
                    : 'block rounded-md px-2 py-1.5 text-sm text-ink-400 hover:bg-ink-800/60 hover:text-white transition-colors'
                }
              >
                {s.title}
              </a>
            ))}
          </nav>
        </aside>

        <article className="docs-prose max-w-none space-y-12">
          {helpSections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="text-2xl font-semibold tracking-tight mb-4 pb-2 border-b border-ink-800">
                {s.title}
              </h2>
              <div className="space-y-3 text-ink-200 leading-relaxed">{s.content}</div>
            </section>
          ))}

          <section id="faq" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold tracking-tight mb-4 pb-2 border-b border-ink-800">
              FAQ
            </h2>
            <div className="space-y-2">
              {helpFaq.map((f) => (
                <details key={f.question} className="rounded-lg border border-ink-800 bg-ink-900 p-4 group">
                  <summary className="font-medium cursor-pointer list-none flex items-center justify-between gap-3 text-white">
                    <span>{f.question}</span>
                    <span className="text-ink-400 group-open:rotate-180 transition-transform">⌄</span>
                  </summary>
                  <div className="mt-3 text-ink-300 text-sm leading-relaxed">{f.answer}</div>
                </details>
              ))}
            </div>
          </section>

          <section id="changelog" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold tracking-tight mb-2 pb-2 border-b border-ink-800">
              История обновлений
            </h2>
            <p className="text-ink-400 text-sm mb-4">
              Возможности к запуску перечислены блоками. Новое — сверху.
            </p>
            <div className="space-y-3">
              {helpChangelog.map((c) => (
                <div key={(c.date ?? '') + c.title} className="rounded-lg border border-ink-800 bg-ink-900 p-4">
                  <div className="flex items-baseline gap-3 flex-wrap mb-3">
                    {c.date && <span className="text-xs font-mono text-ink-400">{c.date}</span>}
                    <span className="font-medium text-white">{c.title}</span>
                  </div>
                  <div className="text-sm text-ink-300 leading-relaxed space-y-2">{c.body}</div>
                </div>
              ))}
            </div>
          </section>

          <div className="border-t border-ink-800 pt-6 mt-12 flex items-start gap-3 text-sm">
            <Mail className="h-4 w-4 text-accent shrink-0 mt-0.5" />
            <div className="text-ink-300">
              Что-то непонятно или нашли баг? Напишите в{' '}
              <a className="text-accent font-medium inline-flex items-center gap-1" href={supportTelegram} target="_blank" rel="noreferrer">
                <Send className="h-3.5 w-3.5" /> чат поддержки в Telegram
              </a>{' '}
              или на{' '}
              <a className="text-accent font-medium" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
              . Обычно отвечаем в течение рабочего дня.
            </div>
          </div>
        </article>
      </div>
    </section>
  );
};
